import { BoxArt } from "@loot-os/ui/components/box-art";
import { Card, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import { toast } from "@loot-os/ui/components/sonner";
import { ImageOff } from "lucide-react";
import { useState } from "react";

import type { BoxArtUpload, BoxArtUrls } from "../lib/boxEditor";
import { RasterImageField } from "./StorefrontFields";

/** The six slots in the grid's order: each row a layer, each column a screen size. */
type ArtSlot = "imageUrl" | keyof BoxArtUrls;

const SLOTS: { layer: string; desktop: ArtSlot; mobile: ArtSlot; help: string }[] = [
  {
    layer: "Image",
    desktop: "imageUrl",
    mobile: "mobileImageUrl",
    help: "The box itself, centered in the frame.",
  },
  {
    layer: "Background",
    desktop: "backgroundImageUrl",
    mobile: "mobileBackgroundImageUrl",
    help: "Fills the frame behind the box.",
  },
  {
    layer: "Overlay",
    desktop: "overlayImageUrl",
    mobile: "mobileOverlayImageUrl",
    help: "Drawn over the box, e.g. a frame or glow.",
  },
];

/**
 * The box's art: image, background and overlay, each for desktop and for mobile, as boxed's
 * editor had them. Every slot takes an upload (stored in the stage's tenant-assets bucket and
 * served from its CDN) or a pasted URL, and goes into the draft like any other edit, so the
 * header's Save writes it. An empty mobile slot shows its desktop one on phones.
 */
export function BoxArtCard({
  imageUrl,
  art,
  onChange,
  upload,
  readOnly,
}: {
  imageUrl: string;
  art: BoxArtUrls;
  onChange: (slot: ArtSlot, url: string) => void;
  upload: BoxArtUpload;
  readOnly: boolean;
}) {
  // Which slot an upload is running for, from the pick until the server has checked the file.
  const [uploadingSlot, setUploadingSlot] = useState<ArtSlot | null>(null);
  const urls: Record<ArtSlot, string> = { imageUrl, ...art };

  // A refusal is thrown to the field, which shows it under the slot.
  const uploadTo = async (slot: ArtSlot, file: File) => {
    setUploadingSlot(slot);
    try {
      onChange(slot, await upload(file));
      toast.success("Image uploaded — save to keep it");
    } finally {
      setUploadingSlot(null);
    }
  };

  const field = (slot: ArtSlot, label: string, help: string) => (
    <RasterImageField
      key={slot}
      id={`box-art-${slot}`}
      label={label}
      url={urls[slot]}
      onUrlChange={(url) => onChange(slot, url)}
      onFile={(file) => uploadTo(slot, file)}
      uploading={uploadingSlot === slot}
      disabled={readOnly}
      uploadsAvailable={!readOnly}
      previewClassName="size-full object-contain"
      help={help}
    />
  );

  const desktop = {
    image: imageUrl,
    background: art.backgroundImageUrl,
    overlay: art.overlayImageUrl,
  };
  const mobile = {
    image: art.mobileImageUrl,
    background: art.mobileBackgroundImageUrl,
    overlay: art.mobileOverlayImageUrl,
  };
  const placeholder = <ImageOff className="size-6 text-muted-foreground" />;

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_auto]">
      <Card>
        <CardHeader>
          <CardTitle>Art</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-6 md:grid-cols-2">
          <div className="grid content-start gap-5">
            <h3 className="text-sm font-medium">Desktop</h3>
            {SLOTS.map((slot) => field(slot.desktop, slot.layer, slot.help))}
          </div>
          <div className="grid content-start gap-5">
            <h3 className="text-sm font-medium">Mobile</h3>
            {SLOTS.map((slot) =>
              field(
                slot.mobile,
                `Mobile ${slot.layer.toLowerCase()}`,
                "Empty uses the desktop one.",
              ),
            )}
          </div>
        </CardContent>
      </Card>
      <Card className="h-fit">
        <CardHeader>
          <CardTitle>Preview</CardTitle>
        </CardHeader>
        <CardContent className="flex items-end gap-4">
          <figure className="grid gap-2">
            <BoxArt
              desktop={desktop}
              mobile={mobile}
              show="desktop"
              className="size-48 rounded-lg border"
              fallback={placeholder}
            />
            <figcaption className="text-center text-xs text-muted-foreground">Desktop</figcaption>
          </figure>
          <figure className="grid gap-2">
            <BoxArt
              desktop={desktop}
              mobile={mobile}
              show="mobile"
              className="size-28 rounded-lg border"
              fallback={placeholder}
            />
            <figcaption className="text-center text-xs text-muted-foreground">Mobile</figcaption>
          </figure>
        </CardContent>
      </Card>
    </div>
  );
}
